/* eslint-disable react-hooks/exhaustive-deps */
import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useRoute, useLocation } from 'wouter';
import DynamicTable from '../DynamicTable';
import { IDynamicTableAddButton } from '../../interfaces/tables';
import AlarmForm from './AlarmForm';
import ConfirmDialog from '../ConfirmDialog';
import * as SubpageState from '../../store/recoil/SubpageState';
import { TABLE_ALARM, TABLE_ALARM_LOG } from '../../Services/TableService';
import { URL_ALARM } from '../../router/constants';
import PermissionsService, { PERMISSION_ALARM } from '../../Services/PermissionsService';
import TabComponent from '../TabComponent';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import OnlineHelp from '../OnlineHelp';
import { ITab } from '../TabComponent/interfaces';
import { selectedCustomersAtom } from '../../store/recoil/customers';

export const tabsByPermission = (tabs: string[], active: string): ITab[] => {
  const enabledTabs: ITab[] = [];
  tabs.forEach((tabItem: string) => {
    if (PermissionsService.hasAccess(PERMISSION_ALARM, ['read'])) {
      enabledTabs.push({
        name: tabItem,
        isActive: tabItem === active,
        url: tabItem === TABLE_ALARM ? URL_ALARM : `${URL_ALARM}/log`,
      });
    }
  });
  return enabledTabs;
};

export default function Alarms(): JSX.Element {
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const [location, navigate] = useLocation();
  const [, params] = useRoute(`${URL_ALARM}/edit/:id`);
  const { formatMessage: f } = useIntl();
  const [isSubpageOpened, setIsSubpageOpened] = React.useState<boolean>(false);
  const [refreshTable, setRefreshTable] = React.useState<boolean>(false);
  const [alarmId, setAlarmId] = React.useState<number | null>(null);
  const [customerId, setCustomerId] = React.useState<number | null>(null);
  const tabs = React.useState(tabsByPermission([TABLE_ALARM, TABLE_ALARM_LOG], TABLE_ALARM))[0];
  const [isSubpageButtonClicked, setIsSubpageButtonClicked] = useRecoilState<boolean>(
    SubpageState.subPageCloseButton
  );

  const { setDocumentTitle } = useDocumentTitle();

  const handleClose = React.useCallback(
    (value?: string) => {
      if (value === 'submit') {
        setIsSubpageOpened(false);
        navigate(URL_ALARM);
      } else {
        setIsSubpageButtonClicked(true);
      }
    },
    [setIsSubpageButtonClicked]
  );

  const handleConfirmCancelAction = () => {
    setIsSubpageButtonClicked(false);
  };

  const handleConfirmCancelSubpage = () => {
    setIsSubpageOpened(false);
    setIsSubpageButtonClicked(false);
    navigate(URL_ALARM);
  };

  const handleSubpage = (id: number, isEdited: boolean, customerValue?: number) => {
    if (isEdited && id && customerValue) {
      setIsSubpageOpened(true);
      setAlarmId(id || null);
      setCustomerId(customerValue);
      setRefreshTable(false);
    } else {
      setIsSubpageOpened(true);
      setAlarmId(null);
      setRefreshTable(false);
    }
  };

  const updateTable = () => {
    setRefreshTable(true);
  };

  const buttons: IDynamicTableAddButton = {
    alarm: {
      open: PermissionsService.hasAccess(PERMISSION_ALARM, ['create'], selectedCustomers)
        ? ['addAlarm']
        : [],
    },
  };

  React.useEffect(() => {
    if (location === URL_ALARM) {
      setIsSubpageOpened(false);
      setIsSubpageButtonClicked(false);
      setDocumentTitle(f({ id: 'menu.alarms' }));
    } else if (location === `${URL_ALARM}/add`) {
      setIsSubpageButtonClicked(false);
      setIsSubpageOpened(true);
      const documentTitle = `${f({ id: 'alarms.subpage.addAlarm' })} - ${f({ id: 'menu.alarms' })}`;
      setDocumentTitle(documentTitle);
    } else if (params && location === `${URL_ALARM}/edit/${params.id}`) {
      const documentTitle = `${f({ id: 'alarms.subpage.editAlarm' })} - ${f({
        id: 'menu.alarms',
      })}`;
      setDocumentTitle(documentTitle);
      setAlarmId(Number(params.id) || null);
      setIsSubpageButtonClicked(false);
      setIsSubpageOpened(true);
    }

    return () => {
      setIsSubpageOpened(false);
      setIsSubpageButtonClicked(false);
    };
  }, [location]);

  return (
    <div>
      <div className="col-12" style={{ display: isSubpageOpened ? 'none' : '' }}>
        <div className="row section page-head">
          <h4>
            <FormattedMessage id="menu.alarms" />
            <OnlineHelp topic="alarms" />
          </h4>
        </div>
        <TabComponent tabs={tabs}>
          <div>
            <DynamicTable
              name={TABLE_ALARM}
              updateTable={refreshTable}
              handleForm={handleSubpage}
              buttons={buttons[TABLE_ALARM]}
              mustClosed={true}
            />

            {isSubpageButtonClicked && (
              <ConfirmDialog
                title={f({ id: 'table.confirm.dialog.alarm.subpage.close.title' })}
                cancelCallback={handleConfirmCancelAction}
                continueCallback={handleConfirmCancelSubpage}
                cancelButtonTitle={f({ id: 'confirm.dialog.answerNo' })}
                continueButtonTitle={f({ id: 'confirm.dialog.answerYes' })}
              >
                {f({ id: 'table.confirm.dialog.alarm.subpage.close.message' })}
              </ConfirmDialog>
            )}
          </div>
        </TabComponent>
      </div>
      {isSubpageOpened && (
        <div>
          <AlarmForm
            id={alarmId || undefined}
            customerId={customerId || undefined}
            updateTable={updateTable}
            closeCallback={handleClose}
          />
        </div>
      )}
    </div>
  );
}
